import type * as React from 'react'

import { cn } from '../../lib/utils'
import { PageHeaderLayoutProvider } from './page-header'

/**
 * Every screen's outermost element: the page's block rhythm.
 *
 * The app shell owns the inline inset (32px, 16px below 900px) and the 1360px
 * column, so raw screens receive it too. PageShell adds 24px above the page
 * title and room below the last section (DESIGN.md "Layout"). `bleed` cancels
 * the shell's inline inset for work that must reach the edge, such as a
 * sticky toolbar or a full-width grid. `wide` lifts the 1360px column for a
 * list or table screen: a data grid uses the full width, while forms and
 * detail pages keep the column so their lines stay readable.
 *
 * The page and its sections share one paper surface (`--card`, set by the
 * shell), so PageShell paints no background of its own. A screen that passes
 * `bg-background` repaints the old grey page; drop it.
 */
function PageShell({
  className,
  bleed = false,
  wide = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  /** Block padding only, and the shell's inline inset cancelled, for edge-bound work. */
  bleed?: boolean
  /** The full content width instead of the 1360px column, for list and table screens. */
  wide?: boolean
}) {
  return (
    <PageHeaderLayoutProvider flushTrail>
      <div
        data-slot="page-shell"
        data-page-width={wide ? 'wide' : undefined}
        className={cn(
          'flex min-h-full min-w-0 flex-col pt-6 pb-24',
          bleed && '-mx-page-inset',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </PageHeaderLayoutProvider>
  )
}

export { PageShell }
